import { OptionType } from '@insurancemenu/imenu-components-lib/dist/types';
import { RateSegment } from '@insurancemenu/imenu-components-lib/dist/types/productCardsTypings';
import { FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { getEffectiveDatesOptions } from 'global/services-helpers/domainService';
import { isGroupSizeInRange } from 'global/utils/browsingCriteriaHelper';
import { makeAutoObservable, reaction } from 'mobx';
import { getRootStoreInstance, RootStore } from 'store/rootStore';
import { DomainProductLineItem, DomainProductLinesDelegate } from '../domainProductLinesDelegate';
import { sunLifeSICAdditionalValidations, sunLifeSICWarningMessage } from './sicCustomValidations';

interface DomainParams {
  rateSegment: RateSegment;
  disableBCFields?: boolean;
}

type SunLifeProposalBaseFields = {
  proposalName: FormControl<string>;
  effectiveDate: FormControl<string>;
  groupSize: FormControl<string>;
  zip: FormControl<string>;
  state: FormControl<string>;
  county: FormControl<string>;
  sic4: FormControl<string>;
};

export class SunLifeProposalFormDelegate {
  private readonly _rootStore: RootStore = getRootStoreInstance();
  public readonly domainProductLinesDelegate: DomainProductLinesDelegate;
  public proposalForm: FormController<SunLifeProposalBaseFields>;

  public get availableProductLineItems(): DomainProductLineItem[] {
    return this.domainProductLinesDelegate.domainProductLineItems;
  }

  public get isAvailableProductLinesLoading(): boolean {
    return this.domainProductLinesDelegate.isLoading;
  }

  public get effectiveDatesOptions(): OptionType[] {
    return getEffectiveDatesOptions(this._rootStore.domainStoreV2.effectiveDates);
  }

  private get _canFetchProductLines(): boolean {
    const {
      fields: { effectiveDate, zip, state, county, sic4 },
    } = this.proposalForm;

    return !!(
      !effectiveDate.unpassedValidationMsg &&
      !zip.unpassedValidationMsg &&
      !state.unpassedValidationMsg &&
      !county.unpassedValidationMsg &&
      !sic4.unpassedValidationMsg
    );
  }

  /** Shown as a warning `Alert` when the current SIC matches a non-blocking rule. */
  public get sicWarningMessage(): string | null {
    const sicValue = this.proposalForm.fields.sic4.value;
    return sicValue && sicValue.length === 4 ? sunLifeSICWarningMessage(sicValue) : null;
  }

  constructor(params: DomainParams) {
    const disableBCFields = params.disableBCFields || false;

    this.proposalForm = FormController.create(
      {
        proposalName: new FormControl<string>('', {
          validations: ['printableCharacters'],
        }),
        effectiveDate: new FormControl<string>('', { disabled: disableBCFields }),
        groupSize: new FormControl<string>('', {
          disabled: disableBCFields,
          customValidations: [
            {
              check: (value: string): boolean => isGroupSizeInRange(value, '2-9'),
              msg: 'Number of Employees should be in range of 2-9 eligible. Please reach out to your Sun Life Small Business Sales Specialists directly.',
            },
          ],
        }),
      },
      {
        capabilities: [
          {
            type: 'zip',
            params: {
              zip: { optional: false, disabled: disableBCFields },
              state: {
                disabled: disableBCFields,
                customValidations: [
                  {
                    check: this._rootStore.domainStoreV2.isEligibleState,
                    msg: 'State is ineligible',
                  },
                ],
              },
              county: { disabled: disableBCFields },
            },
          },
          {
            type: 'sic',
            params: {
              disabled: disableBCFields,
              additionalValidations: sunLifeSICAdditionalValidations,
            },
          },
        ],
      },
    );

    this.domainProductLinesDelegate = new DomainProductLinesDelegate({
      rateSegment: params.rateSegment,
    });

    reaction(
      () => [
        this.proposalForm.fields.effectiveDate.value,
        this.proposalForm.fields.zip.value,
        this.proposalForm.fields.state.value,
        this.proposalForm.fields.county.value,
        this.proposalForm.fields.sic4.value,
      ],
      async () => {
        if (!this._canFetchProductLines) {
          this.domainProductLinesDelegate.clearProductLines();
          return;
        }

        await this.domainProductLinesDelegate.getDomainProductLines(this.proposalForm.getFormDataObject(), true);
      },
    );

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
